import { CheckCircleFilled, EnvironmentOutlined, PaperClipOutlined } from '@ant-design/icons';
import { designTokens } from '@app/theme';
import { formatDateTime } from '@shared/lib/format-date';
import { Button, Card, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import type { TransferConfirmationResponse } from '@api/generated/types.gen';

import { openProof } from '../api';
import { PARTY_ROLES, TRANSFER_KINDS, shortHash, type ConfirmationMatrix } from '../lib';

const { Text } = Typography;

type TransferMatrixProps = {
  agreementId: string;
  matrix: ConfirmationMatrix;
  /** `id актора → телефон` для подписи «кто подтвердил». */
  actorPhones: Record<string, string>;
};

/**
 * Блок «Подтверждения передач»: матрица «выдача/возврат × кредитор/заёмщик».
 *
 * Выдача и возврат считаются состоявшимися только при записях обеих сторон,
 * поэтому отсутствие записи («не подтвердил») выделено визуально — это главный
 * ответ на вопрос поддержки «кто что подтвердил». При наличии пруфа — кнопка
 * открытия файла (стрим с Bearer-токеном), в тултипе — SHA-256 файла
 * с копированием (для сверки с `manifest.json` доказательного пакета);
 * гео показывается текстом.
 */
export function TransferMatrix({ agreementId, matrix, actorPhones }: TransferMatrixProps) {
  const { t } = useTranslation('agreements');

  const renderCell = (c: TransferConfirmationResponse | undefined) => {
    if (!c) {
      return (
        <Text style={{ fontSize: 13, fontWeight: 600, color: designTokens.red600 }}>
          {t('matrix.notConfirmed')}
        </Text>
      );
    }
    const who = c.confirmedBy ? actorPhones[c.confirmedBy] : undefined;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <Text style={{ fontSize: 13, color: designTokens.emerald600, fontWeight: 600 }}>
          <CheckCircleFilled /> {formatDateTime(c.createdAt)}
        </Text>
        {who && (
          <Text
            type="secondary"
            style={{ fontSize: 12, fontFamily: '"JetBrains Mono", monospace' }}
          >
            {who}
          </Text>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          {c.proofFileId && (
            <Tooltip
              title={
                c.proofSha256 ? (
                  <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11 }}>
                    {t('matrix.proofSha256')}:{' '}
                    <Text
                      copyable={{ text: c.proofSha256, tooltips: false }}
                      style={{ color: 'inherit', fontFamily: 'inherit', fontSize: 'inherit' }}
                    >
                      {shortHash(c.proofSha256)}…
                    </Text>
                  </span>
                ) : undefined
              }
            >
              <Button
                type="link"
                size="small"
                icon={<PaperClipOutlined />}
                style={{ padding: 0, height: 'auto', fontSize: 12 }}
                onClick={() => {
                  if (c.proofFileId) void openProof(agreementId, c.proofFileId);
                }}
              >
                {t('matrix.proof')}
              </Button>
            </Tooltip>
          )}
          {c.geo && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <EnvironmentOutlined /> {c.geo}
            </Text>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card
      title={t('matrix.title')}
      style={{ borderColor: designTokens.slate200 }}
      styles={{
        header: { borderBottomColor: designTokens.slate200, fontWeight: 600 },
        body: { padding: 0 },
      }}
    >
      {TRANSFER_KINDS.map((kind, kindIdx) => (
        <div
          key={kind}
          style={{
            padding: '12px 16px',
            borderBlockStart: kindIdx > 0 ? `1px solid ${designTokens.slate100}` : 'none',
          }}
        >
          <Text
            style={{
              display: 'block',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              color: designTokens.slate500,
              marginBlockEnd: 8,
            }}
          >
            {t(`matrix.kinds.${kind}`)}
          </Text>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 12,
            }}
          >
            {PARTY_ROLES.map((role) => (
              <div
                key={role}
                style={{
                  padding: '8px 12px',
                  border: `1px solid ${matrix[kind][role] ? designTokens.slate200 : '#FECACA'}`,
                  background: matrix[kind][role] ? designTokens.slate50 : '#FEF2F2',
                  borderRadius: 6,
                }}
              >
                <Text
                  type="secondary"
                  style={{ display: 'block', fontSize: 12, marginBlockEnd: 4 }}
                >
                  {t(`matrix.roles.${role}`)}
                </Text>
                {renderCell(matrix[kind][role])}
              </div>
            ))}
          </div>
        </div>
      ))}
    </Card>
  );
}
